import { useT } from '@kmgestao/i18n';
import { Badge, PageSectionCard, Skeleton } from '@kmgestao/ui';

import {
  formatCompactInteger,
  formatPercent,
  formatUsd,
  formatWholeNumber,
  type AiKpisModule,
} from '../../../features/ai-analytics/index.js';

export interface ModulesCardProps {
  modules: AiKpisModule[] | undefined;
  totalCalls: number | undefined;
  loading?: boolean;
}

export function ModulesCard({ modules, totalCalls = 0, loading = false }: ModulesCardProps) {
  const { t } = useT('dashboard');
  const { t: tAiAdmin } = useT('ai-admin');

  return (
    <PageSectionCard
      title={t('modules.title')}
      description={t('modules.description')}
      contentClassName="space-y-4"
    >
      {loading
        ? Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))
        : null}
      {!loading && (modules ?? []).length === 0 ? (
        <p className="text-base text-muted-foreground">{t('states.empty')}</p>
      ) : null}
      {!loading ? (
        <ol className="space-y-3">
          {(modules ?? []).map((module, index) => {
            const share = totalCalls > 0 ? (module.callsTotal / totalCalls) * 100 : 0;
            return (
              <li
                key={module.moduleKey}
                className="rounded-xl border border-border/70 bg-muted/20 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={index === 0 ? 'default' : 'outline'}>#{index + 1}</Badge>
                      <p className="text-sm font-semibold sm:text-base">
                        {tAiAdmin(`modules.keys.${module.moduleKey}`, {
                          defaultValue: module.moduleKey,
                        })}
                      </p>
                    </div>
                    <p className="text-sm leading-6 text-muted-foreground">
                      {t('modules.summary', {
                        calls: formatWholeNumber(module.callsTotal),
                        share: formatPercent(share),
                      })}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="font-semibold">{formatCompactInteger(module.tokensTotal)}</p>
                    <p className="text-sm text-muted-foreground">{t('modules.tokens')}</p>
                  </div>
                </div>
                <div
                  className="mt-3 h-2.5 overflow-hidden rounded-full bg-muted"
                  aria-hidden="true"
                >
                  <div
                    className="h-full rounded-full bg-primary transition-[width]"
                    style={{ width: `${Math.max(share, 8)}%` }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">{t('modules.cost')}</span>
                  <span className="font-semibold">{formatUsd(module.costEstimatedUsdTotal)}</span>
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
    </PageSectionCard>
  );
}
